/*
 * Copyright (c) 2024, King Living
 * Dynamic Page Routing per TSD Contentful: Contentful by slug → SFCC category fallback → NotFound (KL-770).
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useLocation} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';

import loadable from '@loadable/component';

import {useCategory, useStores} from '@salesforce/commerce-sdk-react';
import {Center, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin';
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import {useService} from '@amblique/base/app/hooks/use-service';
import {ContentfulService} from '@amblique/contentful/app/services/contentful-service';
import {Page as ContentfulPage} from '@amblique/contentful/app/components/contentful/page';
import Seo from '@amblique/king-living/app/components/seo';
import {useProduct} from '@amblique/king-living/app/hooks/use-product';
import {useBodyPageType} from '@amblique/king-living/app/hooks/use-body-page-type';
import {
  buildCategorySeoQueryKey,
  buildPageBySlugQueryKey,
  buildProductSeoQueryKey,
  usePageBySlug,
} from '@amblique/king-living/app/contentful/hooks';
import {getSlugFromPathname} from '@amblique/king-living/app/utils/slug-from-path';
import {
  buildPageSeoBundle,
  findBlogIdInMainRegion,
  isBlogArticlePage,
  isFaqCmsPage,
  PRODUCT_SEO_EXPAND,
  SEO_MANAGED_META_NAMES,
} from '@amblique/king-living/app/utils/seo-utils';

// Paths that match catch-all but are not app pages (Chrome, PWA Kit internals, etc.)
const SYSTEM_PATH_PATTERN = /^\/(\.well-known|__mrt|favicon\.ico)(\/|$)/;

/** Contentful-style page fallback — centered in main, not top-left KitKat spinner. */
const contentfulPageFallback = (
  <Center minH="75vh" width="100%">
    <Skeleton borderRadius="full" boxSize="12" />
  </Center>
);

const PageNotFound = loadable(() => import('@salesforce/retail-react-app/app/pages/page-not-found'));
const ProductList = loadable(() => import('@amblique/king-living/app/pages/product-list'), {
  fallback: contentfulPageFallback,
});

function DynamicPageSeo({children, seoBundle}) {
  if (!seoBundle) return null;

  const {jsonLdGraph, ...seoProps} = seoBundle;

  return (
    <Seo {...seoProps} jsonLdGraph={jsonLdGraph ?? null}>
      {children}
    </Seo>
  );
}

DynamicPageSeo.propTypes = {
  children: PropTypes.node,
  seoBundle: PropTypes.object,
};

/**
 * Catch-all handler: Contentful page by slug → SFCC category fallback (path → id) → NotFound.
 * Validates page.categoryId / productId / storeId in SFCC before rendering (KL-770).
 */
function DynamicPage() {
  const location = useLocation();
  const pathname = location.pathname;
  const appOrigin = useAppOrigin();
  const {res} = useServerContext();
  const {locale, site} = useMultiSite();
  const currency = locale?.preferredCurrency || 'AUD';

  const isSystemPath = useMemo(
    () => typeof pathname === 'string' && SYSTEM_PATH_PATTERN.test(pathname),
    [pathname]
  );

  const slug = useMemo(() => getSlugFromPathname(pathname), [pathname]);
  const slugPrimary = slug || 'home';

  const {
    data: page,
    isLoading: pageLoading,
    isFetched: pageFetched,
  } = usePageBySlug(slugPrimary, {
    queryOptions: {enabled: !isSystemPath},
  });

  const contentfulService = useService(ContentfulService);
  const queryClient = useQueryClient();

  const pageQueryKey = useMemo(() => {
    if (!contentfulService) return null;

    return buildPageBySlugQueryKey({contentfulService, site, slug: slugPrimary});
  }, [contentfulService, site, slugPrimary]);

  const cachedPage = pageQueryKey ? queryClient.getQueryData(pageQueryKey) : undefined;

  // SFCC category IDs use hyphens (e.g. bedroom-beds); path segments are joined with -
  const categoryIdFromPath = useMemo(
    () => (slug ? slug.replace(/\//g, '-') : ''),
    [slug]
  );
  // Skip category fallback for /page/* (blog, content) – not SFCC categories, avoids bogus API 404
  const isContentPath = slug?.startsWith('page/') ?? false;
  // Legacy `/product/:id` URLs resolve only via Contentful slug (e.g. product/sku); never SFCC PLP fallback
  const isProductPath = slug?.startsWith('product/') ?? false;
  const tryCategoryFallback =
    pageFetched && !page && !!categoryIdFromPath && !isContentPath && !isProductPath;

  const {data: categoryFromPath, isFetched: categoryFallbackFetched} = useCategory(
    {parameters: {id: categoryIdFromPath}},
    {enabled: tryCategoryFallback}
  );

  const categoryId = page?.categoryId ?? cachedPage?.categoryId;
  const productId = page?.productId ?? cachedPage?.productId;
  const storeId = page?.storeId ?? cachedPage?.storeId;

  const categoryQueryKey = useMemo(() => {
    if (!categoryId) return null;

    return buildCategorySeoQueryKey({categoryId, locale, site});
  }, [categoryId, locale, site]);

  const prefetchedCategory = categoryQueryKey ? queryClient.getQueryData(categoryQueryKey) : undefined;

  const {data: categoryForPage, isFetched: categoryFetched} = useCategory(
    {parameters: {id: categoryId}},
    {enabled: !!categoryId}
  );

  const categoryForPageValidation = categoryForPage ?? prefetchedCategory;

  const {data: productForPage, error: productError, isFetched: productFetched} = useProduct(
    {
      parameters: {
        id: productId,
        perPricebook: true,
        expand: PRODUCT_SEO_EXPAND,
        allImages: true,
      },
    },
    {enabled: !!(page || (!pageFetched && cachedPage)) && !!productId}
  );

  const productForSeo = useMemo(() => {
    if (!productId) return undefined;
    if (productForPage) return productForPage;

    const cachedProduct = queryClient.getQueryData(
      buildProductSeoQueryKey({locale, productId, site})
    );

    if (cachedProduct) return cachedProduct;

    const prefetched = res?.locals?.__seoProductPrefetch;
    const prefetchedId = prefetched?.id ?? prefetched?.productId;

    return prefetched && prefetchedId === productId ? prefetched : undefined;
  }, [productForPage, productId, locale, site, queryClient, res]);

  const extraPageMetaTags = useMemo(
    () => (productForSeo?.pageMetaTags ?? []).filter(({id}) => !SEO_MANAGED_META_NAMES.has(id)),
    [productForSeo?.pageMetaTags]
  );

  const {data: storesDataForPage, error: storeError, isFetched: storesFetched} = useStores(
    {parameters: {ids: storeId}},
    {enabled: !!page && !!storeId}
  );

  const validationLoading = useMemo(() => {
    if (!page) return false;
    if (categoryId && !categoryFetched) return true;
    if (productId && !productFetched) return true;
    if (storeId && !storesFetched) return true;

    return false;
  }, [page, categoryId, categoryFetched, productId, productFetched, storeId, storesFetched]);

  const hasStoreError =
    !!storeId && storesFetched && (storeError || !storesDataForPage?.data?.length);

  const shouldShowNotFoundForPage = useMemo(() => {
    if (!page) return false;
    if (categoryId && categoryFetched && !categoryForPageValidation) return true;
    if (productId && productFetched && (productError || !productForPage) && !productForSeo) return true;
    if (hasStoreError) return true;

    return false;
  }, [page, categoryId, categoryForPageValidation, categoryFetched, productId, productError, productForPage, productForSeo, productFetched, hasStoreError]);

  const pageForSeo = page ?? (!pageFetched ? cachedPage : null);

  const faqQuestions = useMemo(
    () => res?.locals?.__seoFaqQuestions,
    [res]
  );

  const blogForSeo = res?.locals?.__seoBlogPrefetch;

  const isSeoReady = useMemo(() => {
    if (tryCategoryFallback) return categoryFallbackFetched;
    if (!pageForSeo) return false;
    if (productId && !productForSeo && !productFetched) return false;
    if (categoryId && !categoryForPageValidation && !categoryFetched) return false;
    if (isFaqCmsPage(pageForSeo) && typeof window === 'undefined' && !faqQuestions?.length) {
      return false;
    }
    if (
      isBlogArticlePage(pageForSeo) &&
      findBlogIdInMainRegion(pageForSeo) &&
      typeof window === 'undefined' &&
      !blogForSeo
    ) {
      return false;
    }

    return true;
  }, [
    tryCategoryFallback,
    categoryFallbackFetched,
    pageForSeo,
    productId,
    productForSeo,
    productFetched,
    categoryId,
    categoryForPageValidation,
    categoryFetched,
    faqQuestions,
    blogForSeo
  ]);

  const pageSeoBundle = useMemo(
    () => (pageForSeo ?
      buildPageSeoBundle({
        appOrigin,
        blog: blogForSeo,
        category: categoryForPageValidation,
        currency,
        faqQuestions,
        page: pageForSeo,
        pathname,
        product: productForSeo,
        siteId: site?.id,
      }) :
      null),
    [pageForSeo, categoryForPageValidation, productForSeo, blogForSeo, faqQuestions, currency, appOrigin, pathname, site?.id]
  );

  const categoryFallbackSeoBundle = useMemo(
    () => (tryCategoryFallback ?
      buildPageSeoBundle({
        appOrigin,
        category: categoryFromPath || {id: categoryIdFromPath},
        currency,
        pathname,
        siteId: site?.id,
      }) :
      null),
    [
      tryCategoryFallback,
      categoryFromPath,
      categoryIdFromPath,
      appOrigin,
      pathname,
      currency,
      site?.id
    ]
  );

  // Apply page--404 to body when the page resolves to NotFound (no Contentful page + no category fallback,
  // or Contentful page found but SFCC validation failed).
  const isNotFound = useMemo(() => {
    if (isSystemPath) return false;
    if (page && shouldShowNotFoundForPage) return true;
    if (pageFetched && !page && !tryCategoryFallback) return true;
    if (tryCategoryFallback && categoryFallbackFetched && !categoryFromPath) return true;

    return false;
  }, [isSystemPath, page, shouldShowNotFoundForPage, pageFetched, tryCategoryFallback, categoryFallbackFetched, categoryFromPath]);

  useBodyPageType(isNotFound ? '404' : null);

  if (isSystemPath) {
    return <PageNotFound />;
  }

  if (pageLoading && !pageForSeo) return contentfulPageFallback;
  if (page && shouldShowNotFoundForPage) return <PageNotFound />;
  if (pageForSeo) {
    if (!isSeoReady) return contentfulPageFallback;

    return (
      <>
        <DynamicPageSeo seoBundle={pageSeoBundle}>
          {extraPageMetaTags.map(({id, value}) => (
            <meta key={id} content={value} name={id} />
          ))}
        </DynamicPageSeo>
        {page && (validationLoading ? contentfulPageFallback : <ContentfulPage page={page} suppressSeo />)}
      </>
    );
  }

  if (tryCategoryFallback) {
    if (!categoryFallbackFetched) return contentfulPageFallback;
    if (!categoryFromPath) return <PageNotFound />;

    return (
      <>
        <DynamicPageSeo seoBundle={categoryFallbackSeoBundle} />
        <ProductList categoryId={categoryIdFromPath} />
      </>
    );
  }

  if (pageFetched && !page) return <PageNotFound />;

  return contentfulPageFallback;
}

DynamicPage.displayName = 'DynamicPage';

export default DynamicPage;
